import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import Sidebar from "../../components/Sidebar";
import { BillingSidebar } from "../../components/billing/BillingSidebar";
import { AccountingTabs } from "../../components/accounting/AccountingTabs";
import { formatINR } from "../../components/dashboard/formatINR";
import ExpenseAPI, {
  ExpenseCategory,
  RecurringExpenseTemplate,
  RecurringFrequency,
} from "../../services/expense.service";

const FREQUENCIES: { value: RecurringFrequency; label: string }[] = [
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "yearly", label: "Yearly" },
];

interface FormState {
  category_id: string;
  amount: string;
  vendor_name: string;
  frequency: RecurringFrequency;
  day_of_period: string;
  start_date: string;
}

const emptyForm = (): FormState => ({
  category_id: "", amount: "", vendor_name: "", frequency: "monthly",
  day_of_period: "1", start_date: new Date().toISOString().slice(0, 10),
});

export const RecurringExpensesPage: React.FC = () => {
  const [templates, setTemplates] = useState<RecurringExpenseTemplate[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [saving, setSaving] = useState(false);

  const categoryName = useCallback(
    (id: string) => categories.find(c => c.category_id === id)?.name ?? "—",
    [categories]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [templateList, categoryList] = await Promise.all([
        ExpenseAPI.listRecurringTemplates(),
        ExpenseAPI.listCategories(),
      ]);
      setTemplates(templateList);
      setCategories(categoryList);
    } catch (err: any) {
      toast.error(err?.message || "Failed to load recurring templates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (evt: React.FormEvent) => {
    evt.preventDefault();
    const amountNum = parseFloat(form.amount);
    if (!form.category_id || !form.amount || isNaN(amountNum) || amountNum <= 0) {
      toast.error("Category and a valid amount are required");
      return;
    }
    setSaving(true);
    try {
      await ExpenseAPI.createRecurringTemplate({
        category_id: form.category_id,
        amount_paise: Math.round(amountNum * 100),
        vendor_name: form.vendor_name || undefined,
        frequency: form.frequency,
        day_of_period: parseInt(form.day_of_period, 10) || 1,
        start_date: form.start_date,
      });
      toast.success("Recurring template created");
      setForm(emptyForm());
      setFormOpen(false);
      load();
    } catch (err: any) {
      toast.error(err?.message || "Failed to create template");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (template: RecurringExpenseTemplate) => {
    try {
      await ExpenseAPI.updateRecurringTemplate(template.template_id, { is_active: !template.is_active });
      load();
    } catch (err: any) {
      toast.error(err?.message || "Failed to update template");
    }
  };

  const handleDelete = async (template: RecurringExpenseTemplate) => {
    if (!window.confirm(`Delete this recurring template (${formatINR(template.amount_paise)} ${template.frequency})?`)) return;
    try {
      await ExpenseAPI.deleteRecurringTemplate(template.template_id);
      toast.success("Template deleted");
      load();
    } catch (err: any) {
      toast.error(err?.message || "Failed to delete template");
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f5f7]">
      <Sidebar />
      <div className="lg:pl-[var(--sidebar-w)] transition-[padding-left] duration-300 ease-in-out pt-14 lg:pt-0 min-h-screen">
        <div className="max-w-[1300px] mx-auto px-4 py-6">
          <div className="bg-white/60 border border-[rgba(206,250,254,0.5)] rounded-[20px] shadow-[0px_10px_15px_rgba(0,0,0,0.1)] overflow-hidden flex flex-col md:flex-row min-h-[calc(100vh-7rem)]">

            {/* Billing sub-navigation */}
            <BillingSidebar />

            {/* Main content */}
            <div className="flex-1 px-4 sm:px-8 py-6 sm:py-8 overflow-y-auto">
              <div className="flex flex-col gap-6 max-w-[920px]">

                <AccountingTabs />

                {/* ── Recurring Expense Templates ─────────────────────────────── */}
                <section className="bg-white/60 border border-[rgba(206,250,254,0.5)] rounded-[14px] shadow-[0px_4px_6px_rgba(0,0,0,0.1)] p-6">
                  <div className="flex items-center justify-between mb-5">
                    <h2 className="text-base font-semibold text-[#0f172b] flex items-center gap-2">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#00b8db" strokeWidth="2"><path d="M21 2v6h-6"/><path d="M3 12a9 9 0 0 1 15-6.7L21 8"/><path d="M3 22v-6h6"/><path d="M21 12a9 9 0 0 1-15 6.7L3 16"/></svg>
                      Recurring Expense Templates
                    </h2>
                    <button
                      onClick={() => setFormOpen(o => !o)}
                      className="py-2 px-4 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-[#00b8db] to-[#2b7fff] hover:opacity-90 transition shadow-sm"
                    >
                      {formOpen ? "Cancel" : "+ New Template"}
                    </button>
                  </div>
                  <p className="text-xs text-slate-400 -mt-3 mb-5">
                    A nightly job generates one expense per template per period — never duplicates a period already generated.
                  </p>

                  {formOpen && (
                    <form onSubmit={handleCreate} className="rounded-[14px] border border-[#cefafe] bg-white/60 p-5 mb-5 space-y-4">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-[#0a0a0a] mb-1">Category</label>
                          <select
                            className="w-full px-3 py-2 text-base sm:text-sm bg-white/50 border border-[#cefafe] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00b8db]/30"
                            value={form.category_id}
                            onChange={e => setForm(f => ({ ...f, category_id: e.target.value }))}
                          >
                            <option value="">Select category…</option>
                            {categories.map(c => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-[#0a0a0a] mb-1">Amount (₹)</label>
                          <input
                            type="number" step="0.01" min="0"
                            className="w-full px-3 py-2 text-base sm:text-sm bg-white/50 border border-[#cefafe] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00b8db]/30"
                            value={form.amount}
                            onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-[#0a0a0a] mb-1">Vendor</label>
                          <input
                            className="w-full px-3 py-2 text-base sm:text-sm bg-white/50 border border-[#cefafe] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00b8db]/30"
                            value={form.vendor_name}
                            onChange={e => setForm(f => ({ ...f, vendor_name: e.target.value }))}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-[#0a0a0a] mb-1">Frequency</label>
                          <select
                            className="w-full px-3 py-2 text-base sm:text-sm bg-white/50 border border-[#cefafe] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00b8db]/30"
                            value={form.frequency}
                            onChange={e => setForm(f => ({ ...f, frequency: e.target.value as RecurringFrequency }))}
                          >
                            {FREQUENCIES.map(fq => <option key={fq.value} value={fq.value}>{fq.label}</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-[#0a0a0a] mb-1">Day of period</label>
                          <input
                            type="number" min="0" max="31"
                            className="w-full px-3 py-2 text-base sm:text-sm bg-white/50 border border-[#cefafe] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00b8db]/30"
                            value={form.day_of_period}
                            onChange={e => setForm(f => ({ ...f, day_of_period: e.target.value }))}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-[#0a0a0a] mb-1">Start date</label>
                          <input
                            type="date"
                            className="w-full px-3 py-2 text-base sm:text-sm bg-white/50 border border-[#cefafe] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00b8db]/30"
                            value={form.start_date}
                            onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
                          />
                        </div>
                        <p className="col-span-2 text-xs text-slate-400">
                          The first occurrence fires exactly on Start date. Day of period only determines the date of each occurrence after the first.
                        </p>
                      </div>
                      <div className="flex justify-end">
                        <button
                          type="submit" disabled={saving}
                          className="py-2 px-4 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-[#00b8db] to-[#2b7fff] hover:opacity-90 transition shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {saving ? "Saving…" : "Create Template"}
                        </button>
                      </div>
                    </form>
                  )}

                  <div className="rounded-[14px] border border-[#cefafe] bg-white/60 overflow-hidden">
                    {loading ? (
                      <div className="p-8 text-center text-slate-400 text-sm">Loading…</div>
                    ) : templates.length === 0 ? (
                      <div className="p-8 text-center text-slate-400 text-sm">No recurring templates yet.</div>
                    ) : (
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-[#cefafe] text-left text-slate-500">
                            <th className="px-4 py-3 font-medium">Category</th>
                            <th className="px-4 py-3 font-medium">Vendor</th>
                            <th className="px-4 py-3 font-medium">Frequency</th>
                            <th className="px-4 py-3 font-medium">Next run</th>
                            <th className="px-4 py-3 font-medium text-right">Amount</th>
                            <th className="px-4 py-3 font-medium text-center">Active</th>
                            <th className="px-4 py-3 font-medium text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {templates.map(t => (
                            <tr key={t.template_id} className="border-b border-[#e6fbfd] last:border-0 hover:bg-[#f0fdfe]">
                              <td className="px-4 py-3 text-slate-700">{categoryName(t.category_id)}</td>
                              <td className="px-4 py-3 text-slate-700">{t.vendor_name || "—"}</td>
                              <td className="px-4 py-3 text-slate-500 capitalize">{t.frequency}</td>
                              <td className="px-4 py-3 text-slate-500">{t.next_run_date}</td>
                              <td className="px-4 py-3 text-right font-medium text-slate-900">{formatINR(t.amount_paise)}</td>
                              <td className="px-4 py-3 text-center">
                                <button
                                  onClick={() => handleToggleActive(t)}
                                  className={`text-xs px-2 py-1 rounded-full ${t.is_active ? "bg-green-50 text-green-600" : "bg-slate-100 text-slate-500"}`}
                                >
                                  {t.is_active ? "Active" : "Paused"}
                                </button>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <button onClick={() => handleDelete(t)} className="text-red-500 hover:underline text-xs">Delete</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </section>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecurringExpensesPage;
